import { useSyncExternalStore } from "react";
import { useTaskActivity } from "@api/agentRun.api";
import type { AgentRunSummary } from "@common/schemas/domains/agentRuns/agent-run.schema";
import type { TaskActivityEvent } from "@common/schemas/domains/agentRuns/task-activity.schema";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import { useBusyClosesDemoRunRecord } from "@components/demo/busyClosesDemo/useBusyClosesDemoLive";
import {
    getAgentRunStatus,
    isCurrentStateEvent,
} from "@components/mainInterface/chat/agent/agentRunStatus";
import { useAgentRunTaskPath } from "@components/mainInterface/chat/agent/agentRunPaths";
import {
    getAgentRunStatusItem,
    toTaskActivityItem,
    useMembersById,
} from "@components/mainInterface/chat/agent/taskActivityItems";
import { BoardCardActivity } from "@design-system/Board/BoardCardActivity";
import { subscribeToMinuteTick } from "@hooks/useTimeAgo";
import { SubScopeProvider } from "@state/subScopeContext";

const MINUTE_MS = 60_000;

/** The status line, then the run task's history newest first; `history` is null while it loads. */
const AgentRunTimeline = ({
    run,
    history,
}: {
    run: AgentRunSummary;
    history: { events: readonly TaskActivityEvent[]; taskTitle: string } | null;
}) => {
    const status = getAgentRunStatus(run);
    const detail = run.progress?.detail ?? status.label;
    const firstWait = run.waitingOn[0] ?? null;
    const taskPathFor = useAgentRunTaskPath();
    const waitingTaskPath =
        status.kind === "waiting" &&
        firstWait !== null &&
        !isBusyClosesDemoId(firstWait.taskId)
            ? taskPathFor(firstWait.taskId, run.workspaceId)
            : null;
    const membersById = useMembersById({ enabled: true });
    const events = history?.events ?? [];
    const [newest] = events;
    const shownEvents =
        newest !== undefined &&
        isCurrentStateEvent({ status, firstWait, event: newest })
            ? events.slice(1)
            : events;
    const items = [
        getAgentRunStatusItem({ run, status, detail, waitingTaskPath }),
        ...(history === null
            ? []
            : shownEvents.map((event) =>
                  toTaskActivityItem({
                      event,
                      taskTitle: history.taskTitle,
                      membersById,
                      taskPathOf: null,
                  }),
              )),
    ];
    return <BoardCardActivity items={items} />;
};

const AgentRunActivityDrawerBody = ({ run }: { run: AgentRunSummary }) => {
    const { data } = useTaskActivity({ taskId: run.runTaskId, enabled: true });
    return (
        <AgentRunTimeline
            run={run}
            history={
                data === undefined
                    ? null
                    : { events: data.events, taskTitle: data.task.title }
            }
        />
    );
};

/** A busy closes demo run's drawer: its history comes from the live demo store, and its times age each minute. */
const DemoAgentRunActivityDrawerBody = ({ run }: { run: AgentRunSummary }) => {
    const record = useBusyClosesDemoRunRecord(run.id);
    useSyncExternalStore(subscribeToMinuteTick, () =>
        Math.floor(Date.now() / MINUTE_MS),
    );
    return (
        <AgentRunTimeline
            run={run}
            history={
                record === null
                    ? null
                    : { events: record.events, taskTitle: record.taskTitle }
            }
        />
    );
};

/**
 * A board card's drawer for the run whose run task the card shows, in any run
 * state: the live status line on top, then the task's history newest first.
 * Reads under the run's workspace so the activity and members fetches target
 * it from an organization board too. A waiting run's status line links to the
 * task it waits on; history labels stay plain text.
 */
export const AgentRunActivityDrawer = ({ run }: { run: AgentRunSummary }) => (
    <SubScopeProvider value={{ workspaceId: run.workspaceId }}>
        {isBusyClosesDemoId(run.id) ? (
            <DemoAgentRunActivityDrawerBody run={run} />
        ) : (
            <AgentRunActivityDrawerBody run={run} />
        )}
    </SubScopeProvider>
);
